/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState, useEffect } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import WarnIcon from "@material-ui/icons/Warning";
import GoodIcon from "@material-ui/icons/VerifiedUser";
import Slider from "@material-ui/core/Slider";
import Button from "@material-ui/core/Button";

import theme from "../../config/theme";

import t from "../../translations";

import { getMinScaleForMaskedObject, getMaxScale } from "editor/helpers/object";

const Wrap = styled.div`
  display: flex;
  > p {
    margin-right: 10px;
    font-size: 21px;
    display: flex;
    align-items: center;
    > span {
      margin-right: 6px;
      white-space: nowrap;
    }
    > b {
      color: #111;
    }
    > .DPI {
      color: ${props => props.DPIcolor};
    }
    .size {
      color: #757575;
    }
  }
  @media screen and (max-width: 480px) {
    b {
      display: none;
    }
  }
`;

const Dimensions = props => {
  const { obj } = props;
  const [scale, setScale] = useState(obj.scaleX);

  useEffect(() => {
    setScale(obj.scaleX);
  }, [obj]);
  if (obj.type !== "image") {
    return "";
  }

  const { width, height } = obj.size;
  const { scaleX, scaleY, isVector, mask } = obj;
  //  real image to scaled koef

  const k = width / obj.width;
  const Wcm = ((width / k) * scaleX * 0.1).toFixed(1);
  const Hcm = ((height / k) * scaleY * 0.1).toFixed(1);

  const dpiK = 10 / 2.54;

  const needPixes = width * scaleX * dpiK;
  const DPI = (100 * width * k) / needPixes;

  let DPIcolor = theme.palette.error.main;
  let DPIValue = DPI.toFixed(0);
  let DPIIcon = <WarnIcon />;
  let DPIText = t.goodDPIdesc;
  if (DPI < 75) {
    DPIText = t.badDPIdesc;
    DPIcolor = theme.palette.error.dark;
    DPIIcon = (
      <WarnIcon
        css={css`
          color: ${DPIcolor};
        `}
      />
    );
  }
  if (DPI >= 75 && DPI < 90) {
    DPIText = t.badDPIdesc;
    DPIcolor = theme.palette.error.main;
    DPIIcon = (
      <WarnIcon
        css={css`
          color: ${DPIcolor};
        `}
      />
    );
  }
  if (DPI >= 90) {
    DPIcolor = theme.palette.secondary.main;
    DPIIcon = (
      <GoodIcon
        css={css`
          color: ${DPIcolor};
        `}
      />
    );
  }

  if (isVector) {
    DPIcolor = theme.palette.secondary.main;
    DPIValue = "";
    DPIIcon = (
      <GoodIcon
        css={css`
          color: ${DPIcolor};
        `}
      />
    );
  }

  if (mask) {
    return (
      <Wrap DPIcolor={DPIcolor}>
        <Typography>Collage zone</Typography>
        <Typography>
          <b>{t.size}:</b>
          <span className="size">{`${Wcm} x ${Hcm} cm`}</span>
        </Typography>
      </Wrap>
    );
  }

  const canvas = obj.canvas;


  const isEditingCollageImage = obj.id === canvas.getActiveObject()?.id && obj.clipId;

  const minScale = getMinScaleForMaskedObject(obj) * 1.05;
  const maxScale = getMaxScale(obj) * 0.95;
  const stepScale = 0.01;

  return (
    <React.Fragment>
      <Wrap DPIcolor={DPIcolor}>
        {!isVector && (
          <Typography>
            <b>{t.resolution}:</b>
            <span className="DPI">{`${DPIValue} ${t.dpi}`}</span>
            <Tooltip title={DPIText} aria-label={DPIText}>
              {DPIIcon}
            </Tooltip>
          </Typography>
        )}
        {!obj.clipPath && (
          <Typography>
            <b>{t.size}:</b>
            <span className="size">{`${Wcm} x ${Hcm} cm`}</span>
          </Typography>
        )}
      </Wrap>
      {obj.clipId && (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            if (!isEditingCollageImage) {
              obj.selectable = true;
              obj.evented = true;
              canvas.setActiveObject(obj);
            } else {
              canvas.discardActiveObject();
            }
          }}
        >
          {isEditingCollageImage ? t.save : t.edit}
        </Button>
      )}
      {obj.clipPath && (
        <Slider
          style={{ maxWidth: 400 }}
          onChange={(e, val) => {
            setScale(val);
            const activeObject = canvas.getActiveObject();
            const ao = activeObject.mask ? obj : activeObject;
            ao.scaleToWidth(ao.width * val);
            ao.setCoords();
            canvas.fire("object:scaling", { target: ao });
            canvas.requestRenderAll();
          }}
          min={minScale}
          max={maxScale}
          step={stepScale}
          value={scale}
        />
      )}
    </React.Fragment>
  );
};

export default Dimensions;
